import React, { useState, useEffect } from 'react';
import { badgeService, Badge, UserStats } from '../services/badgeService';
import { useTranslation } from '../hooks/useTranslation';
import { calculateGlobalStats } from '../utils/habitUtils';
import '../styles/BadgeGallery.css';

interface BadgeGalleryProps {
  onClose: () => void;
}

const BadgeGallery: React.FC<BadgeGalleryProps> = ({ onClose }) => {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const { t } = useTranslation();
  
  useEffect(() => {
    const loadData = async () => {
      // IndexedDB から最新統計を再計算
      const globalStats = await calculateGlobalStats();
      const currentStats = badgeService.getStats();
      const updatedStats = { ...currentStats, ...globalStats };
      
      // サービス側の状態も同期
      await badgeService.updateStats(updatedStats);
      await badgeService.checkBadgeProgress(updatedStats);
      
      setStats(updatedStats);
      setBadges(badgeService.getAllBadges());
    };
    loadData();
  }, []);
  
  const getFilteredBadges = () => {
    if (selectedCategory === 'all') {
      return badges;
    } else if (selectedCategory === 'unlocked') {
      return badges.filter(b => b.isUnlocked);
    } else if (selectedCategory === 'locked') {
      return badges.filter(b => !b.isUnlocked);
    } else {
      return badges.filter(b => b.category === selectedCategory);
    }
  };
  
  const getProgressPercentage = (badge: Badge) => {
    if (!badge.progress) return 0;
    return Math.min((badge.progress / badge.requirement) * 100, 100);
  };
  
  const formatDate = (date?: Date | string) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('ja-JP', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const completionRate = badgeService.getCompletionRate();
  const unlockedCount = badges.filter(b => b.isUnlocked).length;
  const totalCount = badges.length;
  
  return (
    <div className="badge-gallery-overlay" onClick={onClose}>
      <div className="badge-gallery-container" onClick={(e) => e.stopPropagation()}>
        <div className="badge-gallery-header">
          <h2>🏆 {t('badges.title')}</h2>
          <button className="badge-gallery-close" onClick={onClose}>×</button>
        </div>
        
        <div className="badge-stats-summary">
          <div className="stat-card">
            <div className="stat-value">{unlockedCount}/{totalCount}</div>
            <div className="stat-label">{t('badges.unlockedBadges')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{completionRate.toFixed(0)}%</div>
            <div className="stat-label">{t('badges.completionRate')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats?.currentStreak || 0}{t('badges.days')}</div>
            <div className="stat-label">{t('badges.currentStreak')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats?.totalCompleted || 0}{t('badges.times')}</div>
            <div className="stat-label">{t('badges.totalCompleted')}</div>
          </div>
        </div>
        
        <div className="badge-category-filter">
          <button 
            className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
          >
            {t('badges.all')}
          </button>
          <button 
            className={`filter-btn ${selectedCategory === 'unlocked' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('unlocked')}
          >
            {t('badges.unlocked')}
          </button>
          <button 
            className={`filter-btn ${selectedCategory === 'locked' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('locked')}
          >
            {t('badges.locked')}
          </button>
          <button 
            className={`filter-btn ${selectedCategory === 'streak' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('streak')}
          >
            {t('badges.streak')}
          </button>
          <button 
            className={`filter-btn ${selectedCategory === 'milestone' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('milestone')}
          >
            {t('badges.milestone')}
          </button>
          <button 
            className={`filter-btn ${selectedCategory === 'special' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('special')}
          >
            {t('badges.special')}
          </button>
        </div>
        
        <div className="badge-grid">
          {getFilteredBadges().map(badge => (
            <div 
              key={badge.id} 
              className={`badge-card ${badge.isUnlocked ? 'unlocked' : 'locked'}`}
            >
              <div className="badge-icon-container">
                <div className={`badge-icon ${badge.isUnlocked ? 'shine' : 'grayscale'}`}>
                  {badge.icon}
                </div>
                {!badge.isUnlocked && (
                  <div className="badge-lock-overlay">🔒</div>
                )}
              </div>
              
              <div className="badge-info">
                <h3 className="badge-title">{badge.name}</h3>
                <p className="badge-description">{badge.description}</p>
                
                {badge.isUnlocked ? (
                  <div className="badge-unlocked-date">
                    ✅ {formatDate(badge.unlockedAt)}{t('badges.unlockedOn')}
                  </div>
                ) : (
                  <div className="badge-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${getProgressPercentage(badge)}%` }}
                      />
                    </div>
                    <div className="progress-text">
                      {badge.progress || 0} / {badge.requirement}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="badge-footer">
          <div className="achievement-summary">
            <h3>📊 {t('badges.yourRecord')}</h3>
            <div className="achievement-details">
              <div className="achievement-item">
                <span className="achievement-label">{t('badges.longestStreak')}:</span>
                <span className="achievement-value">{stats?.longestStreak || 0}{t('badges.days')}</span>
              </div>
              <div className="achievement-item">
                <span className="achievement-label">{t('badges.habitsCount')}:</span>
                <span className="achievement-value">{stats?.habitsCount || 0}</span>
              </div>
              <div className="achievement-item">
                <span className="achievement-label">{t('badges.joinedDate')}:</span>
                <span className="achievement-value">{formatDate(stats?.joinedDate)}</span>
              </div>
              <div className="achievement-item">
                <span className="achievement-label">{t('badges.perfectWeeks')}:</span>
                <span className="achievement-value">{stats?.perfectWeeks || 0}{t('badges.times')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgeGallery;